import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { MdOutlineDelete, MdEdit } from "react-icons/md";
import { IoMdAdd, IoMdClose } from "react-icons/io";
import axiosWrapper from "../../utils/AxiosWrapper";
import Heading from "../../components/Heading";
import DeleteConfirm from "../../components/DeleteConfirm";
import CustomButton from "../../components/CustomButton";
import Loading from "../../components/Loading";

const FUNDAMENTALS_PLACEHOLDER = `[
  {
    "prompt": "Your question?",
    "hint": "Optional hint",
    "choices": [
      { "key": "A", "text": "First choice" },
      { "key": "B", "text": "Second choice" }
    ],
    "correctKey": "B",
    "explanation": "Why B is correct"
  }
]`;

const DEFAULT_KEYS = ["A", "B", "C", "D"];

const emptyQuestion = () => ({
  prompt: "",
  hint: "",
  explanation: "",
  correctKey: "A",
  choices: DEFAULT_KEYS.map((k) => ({ key: k, text: "" })),
});

const fundamentalsToQuestions = (funds) => {
  if (!Array.isArray(funds) || funds.length === 0) return [emptyQuestion()];
  return funds.map((raw) => {
    const incoming = Array.isArray(raw.choices) ? raw.choices : [];
    let choices = incoming.map((c, i) => ({
      key: String(c.key ?? String.fromCharCode(65 + i))
        .trim()
        .toUpperCase()
        .slice(0, 4) || String.fromCharCode(65 + i),
      text: String(c.text ?? ""),
    }));
    if (choices.length < 2) {
      choices = DEFAULT_KEYS.map((k) => ({ key: k, text: "" }));
    }
    while (choices.length < 4) {
      const k = String.fromCharCode(65 + choices.length);
      choices.push({ key: k, text: "" });
    }
    const ck = String(raw.correctKey || "A")
      .trim()
      .toUpperCase()
      .slice(0, 4);
    return {
      prompt: String(raw.prompt || ""),
      hint: String(raw.hint || ""),
      explanation: String(raw.explanation || ""),
      correctKey: choices.some((c) => c.key === ck) ? ck : choices[0].key,
      choices,
    };
  });
};

function tryParseFundamentalsJson(jsonStr) {
  try {
    const parsed = JSON.parse(jsonStr);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    for (const q of parsed) {
      if (!q.prompt || !q.correctKey || !Array.isArray(q.choices)) return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function buildFundamentalsFromQuestions(questions) {
  const out = [];
  for (let i = 0; i < questions.length; i++) {
    const q = questions[i];
    const prompt = String(q.prompt || "").trim();
    if (!prompt) {
      toast.error(`Question ${i + 1}: add question text`);
      return null;
    }
    const chosen = [];
    const seenKeys = new Set();
    for (const c of q.choices || []) {
      const text = String(c.text || "").trim();
      if (!text) continue;
      let key = String(c.key || "").trim().toUpperCase().slice(0, 8);
      if (!key)
        key = String.fromCharCode(65 + chosen.length).toUpperCase();
      let uniq = key;
      let n = 0;
      while (seenKeys.has(uniq)) {
        uniq = `${key}${++n}`;
      }
      seenKeys.add(uniq);
      chosen.push({ key: uniq, text });
    }
    if (chosen.length < 2) {
      toast.error(`Question ${i + 1}: fill at least two options`);
      return null;
    }
    const ck = String(q.correctKey || "")
      .trim()
      .toUpperCase()
      .slice(0, 8);
    if (!chosen.some((c) => c.key === ck)) {
      toast.error(`Question ${i + 1}: correct answer must match one option key`);
      return null;
    }
    out.push({
      prompt,
      hint: String(q.hint || "").trim(),
      explanation: String(q.explanation || "").trim(),
      correctKey: ck,
      choices: chosen,
    });
  }
  if (out.length === 0) {
    toast.error("Add at least one question");
    return null;
  }
  return out;
}

const emptyForm = () => ({
  title: "",
  description: "",
  weekLabel: "",
  semester: "0",
  branchId: "",
  isActive: true,
  editorMode: "form",
  fundamentalsJson: FUNDAMENTALS_PLACEHOLDER,
  questions: [emptyQuestion()],
});

const branchLabel = (row) => {
  if (row.branchId == null) return "All branches";
  if (typeof row.branchId === "object" && row.branchId.name)
    return row.branchId.name;
  return "—";
};

const branchIdForForm = (row) => {
  const b = row.branchId;
  if (b == null) return "";
  if (typeof b === "object" && b._id != null) return String(b._id);
  return String(b);
};

const BedPracticeSets = () => {
  const userToken = localStorage.getItem("userToken");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${userToken}` }),
    [userToken]
  );

  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const refreshList = () => setListVersion((v) => v + 1);

  const loadBranches = async () => {
    try {
      const res = await axiosWrapper.get(`/branch`, { headers });
      if (res.data.success) setBranches(res.data.data || []);
    } catch (e) {
      if (e.response?.status !== 404) {
        toast.error(e.response?.data?.message || "Failed to load branches");
      }
    }
  };

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  useEffect(() => {
    loadBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await axiosWrapper.get("/bed-practice/admin/sets", {
          headers,
          params: { page, limit: pageSize },
        });
        if (cancelled) return;
        if (!res.data.success) {
          toast.error(res.data.message);
          setRows([]);
          setPagination(null);
          return;
        }
        const d = res.data.data;
        let items = [];
        let meta = null;
        if (d && Array.isArray(d.items)) {
          items = d.items;
          meta = d.pagination || null;
        } else if (Array.isArray(d)) {
          items = d;
          meta = {
            page: 1,
            limit: items.length || pageSize,
            total: items.length,
            totalPages: 1,
            hasNext: false,
            hasPrev: false,
          };
        }
        if (items.length === 0 && page > 1 && meta && meta.total > 0) {
          setPage((p) => Math.max(1, p - 1));
          return;
        }
        setRows(items);
        setPagination(meta);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.message || "Failed to load sets");
          setRows([]);
          setPagination(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, listVersion, userToken, headers]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row._id);
    const qs = fundamentalsToQuestions(row.fundamentals);
    setForm({
      title: row.title || "",
      description: row.description || "",
      weekLabel: row.weekLabel || "",
      semester: String(row.semester ?? 0),
      branchId: branchIdForForm(row),
      isActive: row.isActive !== false,
      editorMode: "form",
      fundamentalsJson: JSON.stringify(row.fundamentals || [], null, 2),
      questions: qs,
    });
    setShowModal(true);
  };

  const syncFormToJson = () => {
    const built = buildFundamentalsFromQuestions(form.questions);
    if (!built) return false;
    setForm((f) => ({
      ...f,
      editorMode: "json",
      fundamentalsJson: JSON.stringify(built, null, 2),
    }));
    return true;
  };

  const syncJsonToForm = () => {
    const parsed = tryParseFundamentalsJson(form.fundamentalsJson);
    if (!parsed) {
      toast.error("Fix JSON errors before switching to form");
      return false;
    }
    setForm((f) => ({
      ...f,
      editorMode: "form",
      questions: fundamentalsToQuestions(parsed),
    }));
    return true;
  };

  const resolveFundamentals = () => {
    if (form.editorMode === "json") {
      const parsed = tryParseFundamentalsJson(form.fundamentalsJson);
      if (!parsed) {
        toast.error("Invalid fundamentals JSON — check brackets and commas");
        return null;
      }
      return parsed;
    }
    return buildFundamentalsFromQuestions(form.questions);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    const fundamentals = resolveFundamentals();
    if (!fundamentals) return;

    const body = {
      title: form.title.trim(),
      description: form.description.trim(),
      weekLabel: form.weekLabel.trim(),
      semester: Number(form.semester),
      branchId: form.branchId || null,
      isActive: form.isActive,
      fundamentals,
    };

    try {
      toast.loading(editingId ? "Updating…" : "Creating…");
      const res = editingId
        ? await axiosWrapper.patch(
            `/bed-practice/admin/sets/${editingId}`,
            body,
            { headers }
          )
        : await axiosWrapper.post("/bed-practice/admin/sets", body, { headers });
      toast.dismiss();
      if (res.data.success) {
        toast.success(res.data.message);
        setShowModal(false);
        setEditingId(null);
        setForm(emptyForm());
        refreshList();
      } else toast.error(res.data.message);
    } catch (e) {
      toast.dismiss();
      toast.error(e.response?.data?.message || "Save failed");
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      toast.loading("Deleting…");
      const res = await axiosWrapper.delete(
        `/bed-practice/admin/sets/${deleteId}`,
        { headers }
      );
      toast.dismiss();
      if (res.data.success) {
        toast.success(res.data.message || "Deleted");
        setDeleteId(null);
        refreshList();
      } else toast.error(res.data.message);
    } catch (e) {
      toast.dismiss();
      toast.error(e.response?.data?.message || "Delete failed");
    }
  };

  const patchQuestion = (qi, updater) => {
    setForm((f) => ({
      ...f,
      questions: f.questions.map((q, i) => (i === qi ? updater(q) : q)),
    }));
  };

  const addChoice = (qi) => {
    patchQuestion(qi, (q) => {
      const letters = q.choices.map((c) => c.key.charCodeAt(0));
      const nextCode = Math.max(65, ...letters, 64) + 1;
      const nk =
        nextCode <= 90
          ? String.fromCharCode(nextCode)
          : `O${q.choices.length + 1}`;
      return {
        ...q,
        choices: [...q.choices, { key: nk, text: "" }],
      };
    });
  };

  const removeChoice = (qi, ci) => {
    patchQuestion(qi, (q) => {
      if (q.choices.length <= 2) return q;
      const next = q.choices.filter((_, i) => i !== ci);
      const still = next.some((c) => c.key === q.correctKey);
      return {
        ...q,
        choices: next,
        correctKey: still ? q.correctKey : next[0].key,
      };
    });
  };

  return (
    <div className="w-full mx-auto mt-10 flex justify-center items-start flex-col mb-10 relative px-4">
      <Heading title="B.Ed practice sets" />
      <p className="text-sm text-gray-600 max-w-2xl mb-4">
        Add MCQs manually (question text, options, correct answer, explanation)
        or use JSON for bulk import. Branch empty = all branches; semester 0 =
        all semesters.
      </p>

      <CustomButton onClick={openCreate} className="mb-6">
        <span className="inline-flex items-center gap-2">
          <IoMdAdd className="text-xl" /> New set
        </span>
      </CustomButton>

      {loading && <Loading />}

      {!loading && (
        <div className="w-full overflow-x-auto">
          <table className="text-sm min-w-full bg-white border border-gray-200">
            <thead>
              <tr className="bg-blue-500 text-white">
                <th className="py-3 px-4 text-left">Title</th>
                <th className="py-3 px-4 text-left">Branch</th>
                <th className="py-3 px-4 text-left">Week</th>
                <th className="py-3 px-4 text-left">Sem</th>
                <th className="py-3 px-4 text-left">Qs</th>
                <th className="py-3 px-4 text-left">Active</th>
                <th className="py-3 px-4 text-center whitespace-nowrap">
                  Edit / Delete
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    {pagination && pagination.total === 0
                      ? 'No sets yet. Use "New set" and add questions (or starter will appear after first student load).'
                      : 'No sets on this page — try Previous or another page.'}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r._id} className="border-t border-gray-100 hover:bg-gray-50/80">
                    <td className="py-3 px-4 font-medium max-w-[200px]">
                      <span className="line-clamp-2">{r.title}</span>
                      {r.systemKey ? (
                        <span className="ml-2 text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                          Built-in
                        </span>
                      ) : null}
                    </td>
                    <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                      {branchLabel(r)}
                    </td>
                    <td className="py-3 px-4">{r.weekLabel || "—"}</td>
                    <td className="py-3 px-4">{r.semester}</td>
                    <td className="py-3 px-4">
                      {(r.fundamentals || []).length}
                    </td>
                    <td className="py-3 px-4">{r.isActive ? "Yes" : "No"}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex justify-center gap-2 flex-wrap">
                        <CustomButton
                          variant="secondary"
                          className="!p-2"
                          title="Edit set"
                          onClick={() => openEdit(r)}
                        >
                          <MdEdit />
                        </CustomButton>
                        <CustomButton
                          variant="danger"
                          className="!p-2"
                          onClick={() => setDeleteId(r._id)}
                          disabled={!!r.systemKey}
                          title={
                            r.systemKey
                              ? "Built-in starter cannot be deleted"
                              : "Delete set"
                          }
                        >
                          <MdOutlineDelete />
                        </CustomButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && pagination && pagination.total > 0 && (
        <div className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4 px-1">
          <p className="text-sm text-gray-600">
            Showing{" "}
            <span className="font-medium tabular-nums">
              {(pagination.page - 1) * pagination.limit + 1}
            </span>
            –
            <span className="font-medium tabular-nums">
              {Math.min(
                pagination.page * pagination.limit,
                pagination.total
              )}
            </span>{" "}
            of{" "}
            <span className="font-medium tabular-nums">{pagination.total}</span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              Per page
              <select
                className="border border-gray-300 rounded-lg px-2 py-1.5 bg-white text-sm"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[5, 10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <CustomButton
              variant="secondary"
              className="!px-3 !py-1.5 text-sm"
              disabled={!pagination.hasPrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </CustomButton>
            <span className="text-sm text-gray-700 tabular-nums px-1">
              Page {pagination.page} / {pagination.totalPages}
            </span>
            <CustomButton
              variant="secondary"
              className="!px-3 !py-1.5 text-sm"
              disabled={!pagination.hasNext}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </CustomButton>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[92vh] my-4 overflow-y-auto shadow-xl border border-gray-100">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">
                {editingId ? "Edit set" : "New set"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-800"
              >
                <IoMdClose className="text-2xl" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  className="w-full border rounded-lg px-3 py-2 min-h-[60px]"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Week label
                  </label>
                  <input
                    className="w-full border rounded-lg px-3 py-2"
                    value={form.weekLabel}
                    onChange={(e) =>
                      setForm({ ...form, weekLabel: e.target.value })
                    }
                    placeholder="e.g. Week 3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Semester (0 = all)
                  </label>
                  <input
                    type="number"
                    min={0}
                    className="w-full border rounded-lg px-3 py-2"
                    value={form.semester}
                    onChange={(e) =>
                      setForm({ ...form, semester: e.target.value })
                    }
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    <strong>0</strong> = every semester sees this set.
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Branch (optional)
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2 bg-white"
                  value={form.branchId}
                  onChange={(e) =>
                    setForm({ ...form, branchId: e.target.value })
                  }
                >
                  <option value="">All branches</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.checked })
                  }
                />
                Active (visible to students)
              </label>

              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  Questions &amp; answers
                </p>
                <div className="flex gap-4 text-sm mb-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="editorMode"
                      checked={form.editorMode === "form"}
                      onChange={() => {
                        if (form.editorMode === "json") syncJsonToForm();
                        else setForm((f) => ({ ...f, editorMode: "form" }));
                      }}
                    />
                    Manual (recommended)
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="editorMode"
                      checked={form.editorMode === "json"}
                      onChange={() => {
                        if (form.editorMode === "form") {
                          syncFormToJson();
                        }
                      }}
                    />
                    Paste JSON
                  </label>
                </div>

                {form.editorMode === "form" ? (
                  <div className="space-y-4">
                    {form.questions.map((q, qi) => (
                      <div
                        key={`q-${qi}`}
                        className="rounded-xl border border-indigo-200 bg-gradient-to-b from-indigo-50/50 to-white p-4 space-y-3"
                      >
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-sm font-bold text-indigo-900">
                            Question {qi + 1}
                          </span>
                          {form.questions.length > 1 ? (
                            <button
                              type="button"
                              className="text-xs text-red-600 font-semibold hover:underline"
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  questions: f.questions.filter(
                                    (_, i) => i !== qi
                                  ),
                                }))
                              }
                            >
                              Remove question
                            </button>
                          ) : null}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Question text
                          </label>
                          <textarea
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                            rows={2}
                            placeholder="Enter the MCQ..."
                            value={q.prompt}
                            onChange={(e) =>
                              patchQuestion(qi, (x) => ({
                                ...x,
                                prompt: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Hint (optional)
                          </label>
                          <input
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                            value={q.hint}
                            onChange={(e) =>
                              patchQuestion(qi, (x) => ({
                                ...x,
                                hint: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <label className="text-xs font-semibold text-gray-700">
                              Options (min 2 with text)
                            </label>
                            <button
                              type="button"
                              className="text-xs font-bold text-indigo-600 hover:underline"
                              onClick={() => addChoice(qi)}
                            >
                              + Add option
                            </button>
                          </div>
                          <div className="space-y-2">
                            {q.choices.map((c, ci) => (
                              <div
                                key={`${qi}-${c.key}-${ci}`}
                                className="flex flex-wrap items-start gap-2"
                              >
                                <input
                                  className="w-12 border rounded-lg px-2 py-1.5 text-sm font-bold uppercase text-center"
                                  maxLength={4}
                                  value={c.key}
                                  title="Option key"
                                  onChange={(e) => {
                                    const v = e.target.value
                                      .toUpperCase()
                                      .slice(0, 8);
                                    patchQuestion(qi, (qu) => {
                                      const nc = qu.choices.map((ch, ix) =>
                                        ix === ci ? { ...ch, key: v } : ch
                                      );
                                      let ck = qu.correctKey;
                                      if (
                                        ck === qu.choices[ci].key &&
                                        v !== ck
                                      )
                                        ck = v || ck;
                                      return {
                                        ...qu,
                                        choices: nc,
                                        correctKey: ck,
                                      };
                                    });
                                  }}
                                />
                                <input
                                  className="flex-1 min-w-[140px] border rounded-lg px-3 py-1.5 text-sm"
                                  placeholder={`Option ${c.key} text`}
                                  value={c.text}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    patchQuestion(qi, (qu) => ({
                                      ...qu,
                                      choices: qu.choices.map((ch, ix) =>
                                        ix === ci ? { ...ch, text: v } : ch
                                      ),
                                    }));
                                  }}
                                />
                                <label className="inline-flex items-center gap-1 text-xs whitespace-nowrap">
                                  <input
                                    type="radio"
                                    name={`correct-${qi}`}
                                    checked={String(q.correctKey) === String(c.key)}
                                    onChange={() =>
                                      patchQuestion(qi, (qu) => ({
                                        ...qu,
                                        correctKey: c.key,
                                      }))
                                    }
                                  />
                                  Correct
                                </label>
                                {q.choices.length > 2 ? (
                                  <button
                                    type="button"
                                    className="text-xs text-red-600 shrink-0"
                                    onClick={() => removeChoice(qi, ci)}
                                  >
                                    ×
                                  </button>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Explanation after submit (shows if wrong — optional but useful)
                          </label>
                          <textarea
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                            rows={2}
                            placeholder="Why the correct answer is right..."
                            value={q.explanation}
                            onChange={(e) =>
                              patchQuestion(qi, (x) => ({
                                ...x,
                                explanation: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    ))}
                    <CustomButton
                      variant="secondary"
                      className="!text-sm"
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          questions: [...f.questions, emptyQuestion()],
                        }))
                      }
                    >
                      <span className="inline-flex items-center gap-2">
                        <IoMdAdd /> Add another question
                      </span>
                    </CustomButton>
                  </div>
                ) : (
                  <div>
                    <textarea
                      className="w-full border rounded-lg px-3 py-2 font-mono text-xs min-h-[260px]"
                      value={form.fundamentalsJson}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          fundamentalsJson: e.target.value,
                        })
                      }
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Paste a JSON array of questions. Fields: prompt, hint,
                      choices [ ]key, text, correctKey, explanation.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t sticky bottom-0 bg-white">
                <CustomButton variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </CustomButton>
                <CustomButton onClick={save}>
                  {editingId ? "Update" : "Create"}
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirm
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        message="Delete this practice set and its attempt history?"
      />
    </div>
  );
};

export default BedPracticeSets;
